import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Content } from "../../components/wrappers/Content";
import { useFetch } from "../../lib/useFetch";
import { fmtMoney, Panel, Stat } from "./shared";
import { XeroConnectionPanel } from "./XeroConnectionPanel";

/** Admin/viewer home (Phase C): pipeline, broker league (90d), funding summary.
 *  The commercial board-pack numbers (Xero P&L, cashflow) arrive in Phase D. */

type AdminHomeData = {
  pipeline: { stage: string; status: string; count: number; totalGbp: number }[];
  league: { broker: string; count: number; totalGbp: number }[];
  confirmedMTD: number;
  funding: { funded: number; awaiting: number; atRisk: number };
};

const STAGE_LABEL: Record<string, string> = {
  enquiry: "Enquiries",
  options_sent: "Options sent",
  offer: "Offers in build",
  negotiating: "Negotiating",
  won: "Won",
};

export default function AdminHome() {
  const { fetch } = useFetch();
  const [data, setData] = useState<AdminHomeData | null>(null);

  useEffect(() => {
    fetch<{ data: AdminHomeData }>("/api/offers/workspace/home-admin")
      .then((r) => setData(r.data))
      .catch(() => null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const inPlay = data?.pipeline
    .filter((p) => p.stage === "negotiating" || p.stage === "options_sent")
    .reduce((s, p) => s + p.totalGbp, 0);
  const maxLeague = Math.max(1, ...(data?.league.map((l) => l.totalGbp) ?? [1]));

  return (
    <Content title="Commercial overview">
      <div className="flex flex-col gap-6 p-1">
        <div className="grid grid-cols-2 gap-4 lg:grid-cols-4">
          <Stat label="Confirmed this month" value={data?.confirmedMTD} detail="all brokers" />
          <Stat label="Value in play" value={inPlay != null ? fmtMoney(inPlay, "GBP") : undefined} detail="options sent + negotiating" />
          <Stat
            label="Departures funded"
            value={data ? `${data.funding.funded}/${data.funding.funded + data.funding.awaiting}` : undefined}
            detail={data ? `${data.funding.awaiting} awaiting balance` : undefined}
            tone={data && data.funding.awaiting > 0 ? undefined : "good"}
          />
          <Stat
            label="At risk"
            value={data?.funding.atRisk}
            detail="departing ≤7d, not fully paid"
            tone={data && data.funding.atRisk > 0 ? "bad" : "good"}
          />
        </div>

        <div className="grid items-start gap-4 lg:grid-cols-2">
          <Panel title="Broker league (90 days)">
            {!data ? (
              <p className="text-sm text-slate-500">Loading…</p>
            ) : data.league.length === 0 ? (
              <p className="text-sm text-slate-500">No confirmed bookings in the last 90 days.</p>
            ) : (
              <table className="w-full text-sm">
                <tbody>
                  {data.league.map((l) => (
                    <tr key={l.broker} className="border-t first:border-t-0">
                      <td className="py-2 pr-3 font-medium">{l.broker}</td>
                      <td className="py-2 pr-3 tabular-nums text-slate-500">{l.count}</td>
                      <td className="w-1/2 py-2">
                        <div className="h-2 rounded bg-slate-100">
                          <div className="h-2 rounded bg-blue-500" style={{ width: `${Math.round((l.totalGbp / maxLeague) * 100)}%` }} />
                        </div>
                        <div className="mt-0.5 text-xs tabular-nums text-slate-400">{fmtMoney(l.totalGbp, "GBP")}</div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </Panel>

          <Panel
            title="Pipeline (120 days)"
            action={
              <Link className="text-sm text-blue-600 hover:underline" to="/queue">
                Open queue
              </Link>
            }
          >
            {!data ? (
              <p className="text-sm text-slate-500">Loading…</p>
            ) : (
              <table className="w-full text-sm">
                <tbody>
                  {data.pipeline.map((p) => (
                    <tr key={p.status} className="border-t first:border-t-0">
                      <td className="py-2 pr-3">{STAGE_LABEL[p.stage] ?? p.stage}</td>
                      <td className="py-2 pr-3 text-right tabular-nums">{p.count}</td>
                      <td className="py-2 text-right tabular-nums text-slate-500">{fmtMoney(p.totalGbp, "GBP")}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </Panel>
        </div>

        <XeroConnectionPanel />
      </div>
    </Content>
  );
}
